import type * as React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useAuthorization } from '@/features/access';
import { useAuth, BrandMark } from '@/features/auth';
import { ErrorState } from '@/shared/components/feedback/error-state';
import { ROUTES } from '@/shared/constants/routes';

/** Full-screen placeholder for the moments before the session is known. */
export function SplashScreen({ label = 'در حال آماده‌سازی…' }: { label?: string }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4" aria-busy>
      <BrandMark size="lg" className="animate-shimmer" />
      <p className="text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/**
 * Gate for everything behind a session.
 *
 * The attempted path is carried in `location.state.from` so a deep link
 * survives the detour through the login screen.
 */
export function RequireAuth({ children }: { children?: React.ReactNode }) {
  const { isAuthenticated, isLoading, error } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return (
      <Navigate to={ROUTES.login} replace state={{ from: location.pathname + location.search }} />
    );
  }

  if (isLoading) return <SplashScreen />;

  // A token that survives but whose profile cannot be loaded (server down,
  // device offline) must not drop the user at the login screen — that would
  // read as "you were signed out" and lose the session for no reason.
  if (error) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <ErrorState error={error} onRetry={() => window.location.reload()} />
      </div>
    );
  }

  return <>{children ?? <Outlet />}</>;
}

/**
 * Keeps signed-in users out of the login screen — except when the mini-app
 * flow deliberately routes there to bind a phone number to an Eitaa identity.
 */
export function RedirectIfAuthenticated({ children }: { children?: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const linkingMiniApp = (location.state as { linkMiniApp?: boolean } | null)?.linkMiniApp;

  if (isAuthenticated && !linkingMiniApp) return <Navigate to={ROUTES.root} replace />;
  return <>{children ?? <Outlet />}</>;
}

/**
 * Gate for the main app: a user with neither a management role nor a unit
 * membership has nothing to see and is sent to onboarding.
 */
export function RequireOnboarded({ children }: { children?: React.ReactNode }) {
  const { profile, isLoading, error } = useAuthorization();

  if (isLoading) return <SplashScreen />;

  if (error) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <ErrorState error={error} onRetry={() => window.location.reload()} />
      </div>
    );
  }

  if (profile === 'newcomer') return <Navigate to={ROUTES.welcome} replace />;
  return <>{children ?? <Outlet />}</>;
}

/**
 * Inverse of {@link RequireOnboarded}: once a user has a role or a membership,
 * the onboarding flow is finished and must not be re-entered.
 */
export function RequireNewcomer({ children }: { children?: React.ReactNode }) {
  const { profile, isLoading } = useAuthorization();

  if (isLoading) return <SplashScreen />;
  if (profile !== 'newcomer') return <Navigate to={ROUTES.root} replace />;
  return <>{children ?? <Outlet />}</>;
}
